/**
 * Embed admin console — list generated post-embeds + revoke a runaway one.
 *
 * The investigate / terminate surface for the embed abuse controls (CLAUDE.md
 * "Embeddable posts — Tracing + abuse controls"). Each embed carries a snowflake
 * token (the snippet's data-embed) that maps to its creator; staff filter by
 * creator and REVOKE — the public payload endpoint then returns 410, so the
 * embed stops rendering on third-party sites within the ≤5-min cache window.
 *
 * Revoke uses a two-step "click again to confirm" (no window.confirm, §0c).
 * All actions are forwarded with the staff secret + the Access email.
 */

import { PageShell } from '../components/ui/PageShell';
import { parseServerDate } from '@postopia/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import { embedsApi, EmbedAdminError, type EmbedRow } from '../lib/embedsApi';

export const Route = createFileRoute('/embeds')({
  component: EmbedsRoute,
});

function EmbedsRoute() {
  return (
    <AdminShell mode="feature" featureName="Embeds">
      <EmbedsConsole />
    </AdminShell>
  );
}

type StatusFilter = 'all' | 'active' | 'revoked';

function ago(iso: string): string {
  const t = parseServerDate(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return parseServerDate(iso).toLocaleDateString();
}

function EmbedsConsole() {
  const [creator, setCreator] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [rows, setRows] = useState<EmbedRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const load = useCallback(
    async (creatorArg: string, statusArg: StatusFilter) => {
      setError(null);
      try {
        setRows(
          await embedsApi.list({
            creator: creatorArg.trim() || undefined,
            status: statusArg,
          }),
        );
      } catch (err) {
        if (err instanceof EmbedAdminError && err.status === 403) {
          setError('Your account does not have admin access.');
        } else if (
          err instanceof EmbedAdminError &&
          err.code === 'embeds_not_configured'
        ) {
          setError('Embed admin is not configured on this environment yet.');
        } else {
          setError("Couldn't load embeds. Try again.");
        }
        setRows([]);
      }
    },
    [],
  );

  // Load on mount + whenever the status filter changes. The creator search
  // commits on Enter / the Search button (so typing doesn't refetch each key).
  useEffect(() => {
    void load(creator, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const revoke = useCallback(
    async (row: EmbedRow) => {
      if (pendingRevoke !== row.id) {
        setPendingRevoke(row.id);
        return;
      }
      setPendingRevoke(null);
      setBusyId(row.id);
      setStatus(null);
      try {
        await embedsApi.revoke(row.id);
        setRows((rs) =>
          rs
            ? rs.map((r) =>
                r.id === row.id
                  ? {
                      ...r,
                      revoked_at: new Date().toISOString(),
                      revoke_reason: 'revoked',
                    }
                  : r,
              )
            : rs,
        );
        setStatus(`Revoked embed ${row.id} — it returns 410 within ~5 min.`);
      } catch {
        setStatus('Revoke failed. Try again.');
      } finally {
        setBusyId(null);
      }
    },
    [pendingRevoke],
  );

  const unrevoke = useCallback(async (row: EmbedRow) => {
    setBusyId(row.id);
    setStatus(null);
    try {
      await embedsApi.unrevoke(row.id);
      setRows((rs) =>
        rs
          ? rs.map((r) =>
              r.id === row.id
                ? { ...r, revoked_at: null, revoke_reason: null }
                : r,
            )
          : rs,
      );
      setStatus(`Restored embed ${row.id}.`);
    } catch (err) {
      setStatus(
        err instanceof EmbedAdminError && err.status === 409
          ? err.message
          : 'Restore failed. Try again.',
      );
    } finally {
      setBusyId(null);
    }
  }, []);

  const card: React.CSSProperties = {
    background: 'var(--c-bg-surface)',
    border: '1px solid var(--c-border-hairline)',
    borderRadius: 10,
    padding: 14,
    display: 'flex',
    gap: 14,
    alignItems: 'flex-start',
  };

  return (
    <PageShell
      title="Embeds"
      description={
        <>
          Every generated embed carries a traceable token (the snippet's <code>data-embed</code>).
          Revoking one makes the public endpoint return 410, and the card stops rendering on
          third-party sites within about 5 minutes.
        </>
      }
    >
      {/* Search + filter */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void load(creator, statusFilter);
        }}
        style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}
      >
        <input
          value={creator}
          onChange={(e) => setCreator(e.target.value)}
          placeholder="Filter by creator @handle or user id"
          style={{
            flex: 1,
            minWidth: 220,
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid var(--c-border-hairline)',
            background: 'var(--c-bg-page)',
            color: 'var(--c-fg-default)',
            fontSize: 14,
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid var(--c-border-hairline)',
            background: 'var(--c-bg-page)',
            color: 'var(--c-fg-default)',
            fontSize: 14,
          }}
        >
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="revoked">Revoked</option>
        </select>
        <button
          type="submit"
          style={{
            padding: '8px 18px',
            borderRadius: 8,
            border: 0,
            background: 'var(--c-fg-default)',
            color: 'var(--c-bg-page)',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Search
        </button>
      </form>

      {status ? (
        <p
          style={{
            fontSize: 13,
            color: 'var(--c-fg-secondary)',
            margin: '0 0 12px',
          }}
        >
          {status}
        </p>
      ) : null}
      {error ? (
        <p style={{ fontSize: 14, color: '#9A2A12', margin: '0 0 12px' }}>
          {error}
        </p>
      ) : null}

      {rows === null ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>
          No embeds {creator ? 'for that creator' : 'yet'}.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((r) => {
            const revoked = !!r.revoked_at;
            const arming = pendingRevoke === r.id;
            const busy = busyId === r.id;
            return (
              <div key={r.id} style={card}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <strong style={{ fontSize: 14, color: 'var(--c-fg-default)' }}>
                      @{r.creator_handle}
                    </strong>
                    <span style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
                      {r.creator_name}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.3,
                        padding: '1px 8px',
                        borderRadius: 999,
                        background: revoked ? '#F4D9D2' : 'var(--c-bg-surface-alt)',
                        color: revoked ? '#9A2A12' : 'var(--c-fg-secondary)',
                      }}
                    >
                      {revoked ? 'REVOKED' : 'Active'}
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: 'var(--c-fg-secondary)',
                      margin: '6px 0 0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.is_article && r.article_headline
                      ? `📰 ${r.article_headline}`
                      : r.post_excerpt || '(no text)'}
                  </p>
                  <p
                    style={{
                      fontSize: 12,
                      color: 'var(--c-fg-tertiary)',
                      margin: '6px 0 0',
                    }}
                  >
                    {/* ALIAS is the public handle (data-embed / the /embed URL)
                        since the 2026-07-23 cutover — it's what you grep a
                        third-party page for. The row id is internal and only
                        used as the revoke key, so it's de-emphasised. */}
                    alias{' '}
                    <button
                      type="button"
                      onClick={() => {
                        if (!r.alias) return;
                        void navigator.clipboard
                          ?.writeText(r.alias)
                          .then(() => setStatus(`Copied alias ${r.alias}`))
                          .catch(() => setStatus('Copy failed — select it manually.'));
                      }}
                      disabled={!r.alias}
                      title={r.alias ? 'Copy alias' : 'Pre-cutover row — no alias'}
                      style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: 12,
                        padding: '1px 6px',
                        borderRadius: 4,
                        border: '1px solid var(--c-border-hairline)',
                        background: 'var(--c-bg-surface-alt)',
                        color: 'var(--c-fg-default)',
                        cursor: r.alias ? 'pointer' : 'default',
                      }}
                    >
                      {r.alias ?? '—'}
                    </button>{' '}
                    · post {r.post_id} · created {ago(r.created_at)} ·{' '}
                    {r.hit_count} miss-loads
                    {r.last_seen_at ? ` · last seen ${ago(r.last_seen_at)}` : ' · never loaded'}
                    {revoked && r.revoke_reason ? ` · ${r.revoke_reason}` : ''}
                  </p>
                </div>
                <div style={{ flexShrink: 0 }}>
                  {revoked ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void unrevoke(r)}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 8,
                        border: '1px solid var(--c-border-hairline)',
                        background: 'transparent',
                        color: 'var(--c-fg-default)',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: busy ? 'default' : 'pointer',
                        opacity: busy ? 0.5 : 1,
                      }}
                    >
                      {busy ? '…' : 'Restore'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void revoke(r)}
                      onBlur={() => arming && setPendingRevoke(null)}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 8,
                        border: 0,
                        background: arming ? '#9A2A12' : '#C7563E',
                        color: '#FAF7F2',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: busy ? 'default' : 'pointer',
                        opacity: busy ? 0.5 : 1,
                      }}
                    >
                      {busy ? '…' : arming ? 'Confirm revoke' : 'Revoke'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}
